import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Typography
} from "@mui/material";
import { useState } from "react";
import { formatUTCDateWithLocalTime } from "../../../utils";
import {
  approveMatchResolutionRequest,
  getMatchResolutionStatusLabel,
  rejectMatchResolutionRequest
} from "../shared";
import {
  BlacklistEntityType,
  BlacklistMatchResolutionRequest,
  BlacklistMatchResolutionRequestStatus
} from "../types";

type PendingAction = "approve" | "reject" | null;

type ReviewMatchResolutionRequestModalProps = {
  open: boolean;
  onClose: () => void;
  request: BlacklistMatchResolutionRequest;
  onActionComplete?: () => Promise<void>;
};

const getEntityTypeLabel = (entityType: BlacklistEntityType): string => {
  switch (entityType) {
    case BlacklistEntityType.Person:
      return "Individual";
    case BlacklistEntityType.Company:
      return "Entity";
    default:
      return "Unknown";
  }
};

const getStatusColor = (status: BlacklistMatchResolutionRequestStatus): "default" | "success" | "warning" | "error" => {
  switch (status) {
    case BlacklistMatchResolutionRequestStatus.Pending:
      return "warning";
    case BlacklistMatchResolutionRequestStatus.Approved:
      return "success";
    case BlacklistMatchResolutionRequestStatus.Rejected:
      return "error";
    default:
      return "default";
  }
};

const ReviewMatchResolutionRequestModal = ({
  open,
  onClose,
  request,
  onActionComplete
}: ReviewMatchResolutionRequestModalProps) => {
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const openBlacklistRecord = () => {
    switch (request.entityType) {
      case BlacklistEntityType.Person:
      case BlacklistEntityType.Company:
        window.open(`/edit-blacklist-name?entityId=${request.entityId}&entityType=${request.entityType}`, "_blank");
        break;
      default:
        window.open(`/view-bulletins?entityType=${request.entityType}&entityId=${request.entityId}`, "_blank");
        break;
    }
  };

  const handleReview = async () => {
    if (!pendingAction) {
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);

    try {
      if (pendingAction === "approve") {
        await approveMatchResolutionRequest(request);
      } else {
        await rejectMatchResolutionRequest(request);
      }

      if (onActionComplete) {
        await onActionComplete();
      }

      setPendingAction(null);
      onClose();
    } catch (error) {
      console.error(`Error attempting to ${pendingAction} match resolution request:`, error);
      setErrorMessage(`An error occurred while trying to ${pendingAction} this request. Please try again.`);
    } finally {
      setIsProcessing(false);
    }
  };

  const confirmTitle = pendingAction === "approve" ? "Approve Request" : "Reject Request";
  const confirmMessage =
    pendingAction === "approve"
      ? "Approving this request will cause future reports to ignore this exact lookup-code and blacklist-record match."
      : "Rejecting this request will leave the match active in future reports.";

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ py: 1 }}>
          Resolve a Contact
          <IconButton aria-label="close" onClick={onClose} sx={{ position: "absolute", right: 8, top: 4 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 1.5 }}>
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {errorMessage}
            </Alert>
          )}

          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
              Match Resolution Request Details
            </Typography>
            <Chip
              label={getMatchResolutionStatusLabel(request.status)}
              color={getStatusColor(request.status)}
              size="small"
            />
          </Box>

          <Grid container spacing={0.75}>
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>Request ID:</strong> {request.id}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>Lookup Code:</strong> {request.lookupCode}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>Blacklist Type:</strong> {getEntityTypeLabel(request.entityType)}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>Blacklist ID:</strong> {request.entityId}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>Requested By:</strong> {request.requestedBy}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>Requested Date:</strong> {formatUTCDateWithLocalTime(request.requestedDate)}
              </Typography>
            </Grid>
            {request.reviewedBy && (
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Reviewed By:</strong> {request.reviewedBy}
                </Typography>
              </Grid>
            )}
            {request.reviewedDate && (
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Reviewed Date:</strong> {formatUTCDateWithLocalTime(request.reviewedDate)}
                </Typography>
              </Grid>
            )}
          </Grid>

          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ mb: 0.25 }}>
              <strong>Reason:</strong>
            </Typography>
            <Typography variant="body2" sx={{ pl: 0.5 }}>
              {request.reason}
            </Typography>
          </Box>

          <Divider sx={{ my: 1.5 }} />

          <Button variant="outlined" size="small" onClick={openBlacklistRecord}>
            View Blacklist Record
          </Button>
        </DialogContent>

        <DialogActions sx={{ py: 0.75 }}>
          {request.status === BlacklistMatchResolutionRequestStatus.Pending ? (
            <>
              <Button color="error" onClick={() => setPendingAction("reject")} disabled={isProcessing} size="small">
                Reject
              </Button>
              <Button variant="contained" onClick={() => setPendingAction("approve")} disabled={isProcessing} size="small">
                Approve
              </Button>
            </>
          ) : (
            <Button onClick={onClose} size="small">
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(pendingAction)} onClose={() => setPendingAction(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{confirmTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingAction(null)} disabled={isProcessing} size="small">
            Cancel
          </Button>
          <Button variant="contained" onClick={handleReview} disabled={isProcessing} size="small">
            {isProcessing ? "Processing..." : pendingAction === "approve" ? "Approve" : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ReviewMatchResolutionRequestModal;
